import './styles/index.css'
import { StrictMode, type ComponentType, type ReactNode } from 'react'
import { createRoot } from 'react-dom/client'
import { setInitData } from '@/shared/api'
import { toLanguage, useLanguageStore } from '@/shared/i18n'
import { applyPlatform, initTelegram } from '@/shared/telegram'
import { applyStoredTheme, syncThemeChrome } from '@/shared/theme'
import { App } from './app'
import { applyLaunchTarget, applyOnboardingTarget } from './launch'
import { setupOfflineVouchers } from './offline-vouchers'
import { setupServiceWorker } from './service-worker'

type Wrapper = ComponentType<{ children: ReactNode }>

type DevEnvironment = {
  Wrapper: Wrapper
  /** Подпись для заголовка: у SDK при подмене своя копия, см. `mock-env.ts`. */
  initData: string | null
}

/**
 * Вне Telegram при разработке окружение подменяется. Динамический импорт под
 * DEV: в прод-сборку ни подмена, ни дев-оверлей не попадают. Исключение —
 * сборка проверки в браузере (`pnpm build:check`, режим `check`): это прод-сборка
 * с Service Worker, которую без подмены вне Telegram было бы не открыть. Условие
 * статическое (`MODE` подставляется при сборке), и обычная `pnpm build` подмену
 * не содержит.
 */
const setupDevEnvironment = async (): Promise<DevEnvironment | null> => {
  if (!import.meta.env.DEV && import.meta.env.MODE !== 'check') {
    return null
  }

  const { setupMockEnv } = await import('./mock-env')
  const mocked = await setupMockEnv()

  if (!mocked) {
    return null
  }

  const { DevChrome } = await import('./dev-chrome')

  return { Wrapper: DevChrome, initData: mocked.initData }
}

const bootstrap = async (): Promise<void> => {
  // До первого рендера и до всего остального: иначе светлая тема начнётся со вспышки тёмной.
  applyStoredTheme()

  const devEnvironment = await setupDevEnvironment()
  const DevWrapper = devEnvironment?.Wrapper ?? null
  const launch = initTelegram()

  // Шрифт платформы — до первого рендера, иначе текст начнётся с прыжка шрифта.
  applyPlatform()

  setInitData(devEnvironment ? devEnvironment.initData : launch.rawInitData)
  useLanguageStore.getState().suggestLanguage(toLanguage(launch.languageCode))
  syncThemeChrome()
  // До первого запроса подарка: чужие ваучеры стираются, свой берётся с устройства.
  setupOfflineVouchers(launch.telegramId)
  // После подписи и языка: запрос подарка уходит сразу, с заголовком и нужным `Accept-Language`.
  const isLinkLaunch = applyLaunchTarget(launch.startParam)

  // Не к подарку и не к обращению — может быть, к онбордингу; первый запуск на новом устройстве
  // ждёт ответа CloudStorage не дольше 0,8 с, повторный — нисколько.
  if (!isLinkLaunch) {
    await applyOnboardingTarget()
  }

  const container = document.getElementById('root')

  if (!container) {
    throw new Error('#root не найден в index.html')
  }

  createRoot(container).render(
    <StrictMode>
      {DevWrapper ? (
        <DevWrapper>
          <App />
        </DevWrapper>
      ) : (
        <App />
      )}
    </StrictMode>,
  )

  setupServiceWorker()
}

void bootstrap()
